import moment from 'moment';
import { AllGraphIntervals } from 'src/v2/components/layout/statistics/graphs/graphTypes';

import {
  getDifferenceInMonths,
  getTimestampAtStartOfNextDay,
  getTimestampAtStartOfNextHour,
  getTimestampAtStartOfNextMonth,
  getTimestampAtStartOfNextWeek,
} from '~utils/date';

const TIME_DATA_KEY = 'timestamp';
const TIME_INTERVAL = 0;
const TIME_HOURLY_FORMAT = 'H:mm';
const TIME_DAILY_FORMAT = 'D MMM';
const TIME_MONTHLY_FORMAT = 'MMM';

export const getTimeDomain = (graphData: Partial<{ timestamp: number }>[]) =>
  [graphData[0].timestamp, graphData[graphData.length - 1].timestamp] as [number, number];

export const getTicks = ({
  graphData,
  tickCount,
  getNextTick,
}: {
  graphData: Partial<{ timestamp: number }>[];
  tickCount: number;
  getNextTick: (timestamp: number) => number;
}) =>
  new Array(tickCount).fill(0).reduce((acc, _, index) => {
    if (index === 0) {
      acc.push(graphData[0].timestamp);
    } else {
      acc.push(getNextTick(acc[index - 1]));
    }
    return acc;
  }, []);

export const getTickFormatter = (format: string) => (val: any) => moment(val).utc().format(format);

export const createTimeTicks = (graphData: Partial<{ timestamp: number }>[], interval: AllGraphIntervals) => {
  let tickCount = 0;
  const domain = getTimeDomain(graphData);
  let getNextTick = getTimestampAtStartOfNextDay;
  let tickFormat = TIME_DAILY_FORMAT;

  switch (interval) {
    case 'Day': {
      tickCount = 7;
      getNextTick = timestamp => getTimestampAtStartOfNextHour(timestamp, 4);
      tickFormat = TIME_HOURLY_FORMAT;
      break;
    }
    case 'Week': {
      tickCount = 7;
      getNextTick = timestamp => getTimestampAtStartOfNextDay(timestamp, 1);
      tickFormat = TIME_DAILY_FORMAT;
      break;
    }
    case 'Month': {
      tickCount = 5;
      getNextTick = getTimestampAtStartOfNextWeek;
      tickFormat = TIME_DAILY_FORMAT;
      break;
    }
    case 'Year': {
      const monthDiff = getDifferenceInMonths(domain[0], domain[1]) + 1;

      if (monthDiff < 13) {
        tickCount = getDifferenceInMonths(domain[0], domain[1]) + 1;
      } else {
        tickCount = 13;
      }

      getNextTick = getTimestampAtStartOfNextMonth;
      tickFormat = TIME_MONTHLY_FORMAT;
      break;
    }
    case 'All': {
      tickCount = getDifferenceInMonths(domain[0], domain[1]) + 1;
      getNextTick = getTimestampAtStartOfNextMonth;
      tickFormat = TIME_MONTHLY_FORMAT;
      break;
    }
  }

  const ticks = getTicks({ graphData, tickCount, getNextTick });

  const tickFormatter = getTickFormatter(tickFormat);

  return {
    ticks,
    tickFormatter,
    dataKey: TIME_DATA_KEY,
    domain,
    interval: TIME_INTERVAL,
  };
};
